const NOSTR_PUBLIC_KEY_PATTERN = /^[0-9a-f]{64}$/;
const NOSTR_EVENT_ID_PATTERN = /^[0-9a-f]{64}$/;
const NOSTR_SIGNATURE_PATTERN = /^[0-9a-f]{128}$/;

export const NOSTR_AUTH_KIND = 27235;
export const NOSTR_AUTH_PATH = '/v1/auth/nostr/nip-07';
export const DEFAULT_API_BASE_URL = 'https://api.theorangepillapp.com';

// Leave enough of the API's 60-second acceptance window for the request itself.
const MAX_PROOF_AGE_BEFORE_POST_SECONDS = 45;
const MAX_SIGNING_ATTEMPTS = 2;

export interface NostrUnsignedEvent {
  kind: number;
  created_at: number;
  content: string;
  tags: string[][];
}

export interface NostrSignedEvent extends NostrUnsignedEvent {
  id: string;
  pubkey: string;
  sig: string;
}

export interface NostrSigner {
  getPublicKey(): Promise<string>;
  signEvent(event: NostrUnsignedEvent): Promise<NostrSignedEvent>;
}

interface CreateNostrAuthProofOptions {
  signer: NostrSigner;
  isSignup: boolean;
  loginUrl: string;
  afterCreatedAt?: number;
  now?: () => number;
  sleep?: (milliseconds: number) => Promise<void>;
}

export interface NostrAuthProof {
  event: NostrSignedEvent;
  selectedPublicKey: string;
}

export class NostrAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NostrAuthError';
  }
}

export const isNostrExtensionInstalled = (): boolean => {
  return typeof window !== 'undefined' && typeof window.nostr !== 'undefined';
};

export const getBrowserNostrSigner = (): NostrSigner | null => {
  return isNostrExtensionInstalled() ? window.nostr ?? null : null;
};

export const normalizeNostrPublicKey = (publicKey: string): string => {
  const normalizedPublicKey = publicKey.trim().toLowerCase();
  if (!NOSTR_PUBLIC_KEY_PATTERN.test(normalizedPublicKey)) {
    throw new NostrAuthError('The Nostr signer returned an invalid public key.');
  }
  return normalizedPublicKey;
};

const isLocalApiHost = (hostname: string): boolean => {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
};

export const getNostrLoginUrl = (
  apiBaseUrl: string | undefined = import.meta.env.VITE_BASE_URL,
): string => {
  let parsedBaseUrl: URL;
  try {
    parsedBaseUrl = new URL(apiBaseUrl?.trim() || DEFAULT_API_BASE_URL);
  } catch {
    throw new NostrAuthError('The configured API URL is invalid.');
  }

  if (parsedBaseUrl.protocol !== 'https:' && !(parsedBaseUrl.protocol === 'http:' && isLocalApiHost(parsedBaseUrl.hostname))) {
    throw new NostrAuthError('The Nostr login API must use HTTPS outside local development.');
  }

  const basePath = parsedBaseUrl.pathname
    .replace(/\/+$/, '')
    .replace(/\/v1$/, '');

  parsedBaseUrl.pathname = `${basePath}${NOSTR_AUTH_PATH}`.replace(/\/{2,}/g, '/');
  parsedBaseUrl.search = '';
  parsedBaseUrl.hash = '';

  return parsedBaseUrl.toString();
};

const tagsMatch = (actual: unknown, expected: string[][]): boolean => {
  if (!Array.isArray(actual) || actual.length !== expected.length) {
    return false;
  }

  return actual.every((tag, tagIndex) => (
    Array.isArray(tag)
    && tag.length === expected[tagIndex].length
    && tag.every((value, valueIndex) => value === expected[tagIndex][valueIndex])
  ));
};

const validateSignedEvent = (
  signedEvent: NostrSignedEvent,
  unsignedEvent: NostrUnsignedEvent,
  selectedPublicKey: string,
): void => {
  if (!signedEvent || typeof signedEvent !== 'object') {
    throw new NostrAuthError('The Nostr signer did not return a signed event.');
  }

  if (
    !NOSTR_EVENT_ID_PATTERN.test(signedEvent.id)
    || !NOSTR_SIGNATURE_PATTERN.test(signedEvent.sig)
    || !NOSTR_PUBLIC_KEY_PATTERN.test(signedEvent.pubkey)
  ) {
    throw new NostrAuthError('The Nostr signer returned an invalid signed event.');
  }

  if (signedEvent.pubkey !== selectedPublicKey) {
    throw new NostrAuthError('The selected Nostr account changed while signing. Please try again.');
  }

  if (
    signedEvent.kind !== unsignedEvent.kind
    || signedEvent.created_at !== unsignedEvent.created_at
    || signedEvent.content !== unsignedEvent.content
    || !tagsMatch(signedEvent.tags, unsignedEvent.tags)
  ) {
    throw new NostrAuthError('The Nostr signer changed the login proof.');
  }
};

const defaultSleep = (milliseconds: number): Promise<void> => (
  new Promise((resolve) => window.setTimeout(resolve, milliseconds))
);

export const createNostrAuthProof = async ({
  signer,
  isSignup,
  loginUrl,
  afterCreatedAt,
  now = Date.now,
  sleep = defaultSleep,
}: CreateNostrAuthProofOptions): Promise<NostrAuthProof> => {
  let selectedPublicKey: string;
  try {
    selectedPublicKey = normalizeNostrPublicKey(await signer.getPublicKey());
  } catch (error) {
    if (error instanceof NostrAuthError) {
      throw error;
    }
    throw new NostrAuthError('Nostr account selection was cancelled or failed.');
  }

  let previousCreatedAt = afterCreatedAt;

  for (let attempt = 0; attempt < MAX_SIGNING_ATTEMPTS; attempt += 1) {
    if (previousCreatedAt !== undefined) {
      const nextAllowedTime = (previousCreatedAt + 1) * 1000;
      const waitTime = nextAllowedTime - now();
      if (waitTime > 0) {
        await sleep(waitTime);
      }

      if (Math.floor(now() / 1000) <= previousCreatedAt) {
        throw new NostrAuthError('Please check your device clock and try again.');
      }
    }

    // This timestamp must be generated immediately before the signer prompt.
    const unsignedEvent: NostrUnsignedEvent = {
      kind: NOSTR_AUTH_KIND,
      created_at: Math.floor(now() / 1000),
      content: '',
      tags: [
        ['u', loginUrl],
        ['method', 'POST'],
        ['action', isSignup ? 'signup' : 'login'],
      ],
    };

    let signedEvent: NostrSignedEvent;
    try {
      signedEvent = await signer.signEvent(unsignedEvent);
    } catch {
      throw new NostrAuthError('Nostr signing was cancelled or failed.');
    }

    validateSignedEvent(signedEvent, unsignedEvent, selectedPublicKey);

    const proofAgeSeconds = Math.floor(now() / 1000) - signedEvent.created_at;
    if (proofAgeSeconds < -30) {
      throw new NostrAuthError('Please check your device clock and try again.');
    }
    if (proofAgeSeconds <= MAX_PROOF_AGE_BEFORE_POST_SECONDS) {
      return { event: signedEvent, selectedPublicKey };
    }

    previousCreatedAt = signedEvent.created_at;
  }

  throw new NostrAuthError('The Nostr login proof expired while signing. Please try again.');
};

declare global {
  interface Window {
    nostr?: NostrSigner;
  }
}
